import PropTypes from "prop-types";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const SELECTED_TANK_STORAGE_KEY = "reef-selected-tank-id";
/** Salifert tests UI sentinel for the shared "all tanks" value (""). */
export const SALIFERT_ALL_TANKS = "__all__";

const LEGACY_SESSION_KEYS = [
  "salifert-tests-selected-tank",
  "salifert-trends-selected-tank",
];

function readStoredTankId() {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    const stored = localStorage.getItem(SELECTED_TANK_STORAGE_KEY);
    if (stored !== null) {
      return stored;
    }
    for (const key of LEGACY_SESSION_KEYS) {
      const legacy = window.sessionStorage.getItem(key);
      if (!legacy) continue;
      return legacy === SALIFERT_ALL_TANKS ? "" : legacy;
    }
  } catch {
    // ignore storage errors
  }
  return "";
}

function writeStoredTankId(tankId) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(SELECTED_TANK_STORAGE_KEY, tankId);
  } catch {
    // ignore storage errors
  }
}

const SelectedTankContext = createContext(undefined);

export function SelectedTankProvider({ children }) {
  const [selectedTankId, setSelectedTankIdState] = useState(readStoredTankId);

  const setSelectedTankId = useCallback((tankId) => {
    const next = tankId == null ? "" : String(tankId);
    setSelectedTankIdState(next);
    writeStoredTankId(next);
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== SELECTED_TANK_STORAGE_KEY) {
        return;
      }
      setSelectedTankIdState(event.newValue ?? "");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({ selectedTankId, setSelectedTankId }),
    [selectedTankId, setSelectedTankId],
  );

  return (
    <SelectedTankContext.Provider value={value}>{children}</SelectedTankContext.Provider>
  );
}

SelectedTankProvider.propTypes = {
  children: PropTypes.node,
};

export function useSelectedTank() {
  const context = useContext(SelectedTankContext);
  if (!context) {
    throw new Error("useSelectedTank must be used within SelectedTankProvider");
  }
  return context;
}

/** MUI Select value: only return a tank id present in `tanks`, else "". */
export function resolveSelectedTankSelectValue(selectedTankId, tanks) {
  if (!selectedTankId) {
    return "";
  }
  if (!Array.isArray(tanks) || tanks.length === 0) {
    return "";
  }
  return tanks.some((tank) => String(tank.id) === String(selectedTankId))
    ? String(selectedTankId)
    : "";
}

/** Clear the shared tank if it no longer exists for the current user. */
export function useValidateSelectedTank(tanks, tanksReady = false) {
  const { selectedTankId, setSelectedTankId } = useSelectedTank();

  useEffect(() => {
    if (!tanksReady || !selectedTankId || !Array.isArray(tanks)) {
      return;
    }
    if (!tanks.some((tank) => String(tank.id) === String(selectedTankId))) {
      setSelectedTankId("");
    }
  }, [selectedTankId, setSelectedTankId, tanks, tanksReady]);
}
